/**
 * EMPTY · LOADING · SLOW · ERROR — as written Hebrew states. 09 §5.8.
 *
 * "Every state is a written Hebrew screen with a title, one sentence of explanation, and one
 *  obvious next step." Never a blank screen, never a full-page spinner that hides where you are,
 *  never an English exception, never a stack trace.
 *
 * Denials do NOT come through here — they have their own component, because a denial carries a
 * server decision and this one does not (see DenialNotice).
 */

import { useState } from 'react';
import { SupportReference } from './SupportReference';
import type { ReactNode } from 'react';

export interface StateBlockProps {
  readonly titleHe: string;
  readonly bodyHe: string;
  readonly tone?: 'empty' | 'error' | 'unavailable' | 'stale';
  readonly nextStep?: ReactNode;
}

export function StateBlock({ titleHe, bodyHe, tone = 'empty', nextStep }: StateBlockProps): JSX.Element {
  return (
    <section className="state-block" data-tone={tone}>
      <h2 className="state-block__title">{titleHe}</h2>
      <p className="state-block__body">{bodyHe}</p>
      {nextStep === undefined ? null : <div className="state-block__next">{nextStep}</div>}
    </section>
  );
}

/**
 * 09 §5.8 Loading: "skeleton rows in the shape of the coming table, with the context bar already
 * populated". The bar is rendered by the shell and is never behind the content (09 §3.3).
 */
export function SkeletonRows({ rows = 6 }: { readonly rows?: number }): JSX.Element {
  return (
    <div className="skeleton" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">הנתונים בטעינה</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton__row" />
      ))}
    </div>
  );
}

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *  A SUPPORT CODE IS NOT A MESSAGE. **Owner instruction, 2026-10-25.**
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Verbatim:
 *
 *     *"קוד שגיאה לבדו אינו מספיק. בכל כשל הצג בעברית פשוטה מה קרה ומה המשתמש צריך לעשות עכשיו.
 *     קוד התמיכה יופיע כמידע משני שאפשר להעתיק."*
 *     *"כשסיבת הכשל אינה ידועה, אמור זאת במפורש, בלי לנחש."*
 *
 * What this used to render was *"אירעה תקלה / לא הצלחנו להשלים את הפעולה"* and a reference code —
 * three facts, two of which the person already knew from the fact that nothing happened. It is the
 * screen the Owner met when MFA enrolment refused, and it told him nothing he could act on.
 *
 * ── THE SHAPE, AND WHY EACH PART IS SEPARATE ───────────────────────────────────────────────────
 *
 *   WHAT HAPPENED   one sentence, true for this failure. A caller that knows more says more.
 *   WHAT TO DO NOW  the action. This is the part that was missing entirely.
 *   SUPPORT CODE    secondary, and copyable — a code nobody can copy is a code read aloud wrong.
 *
 * ── SAYING "WE DO NOT KNOW" IS A FEATURE OF THE DEFAULT ────────────────────────────────────────
 *
 * The defaults below do NOT guess. This component is reached when the server answered in a way the
 * client could not classify, and the honest sentence for that is that the cause is not known —
 * *"בלי לנחש"*. A screen that knows the plausible causes for ITS action passes them in; one that
 * does not gets wording that promises nothing. The temptation is a default that says "probably the
 * connection" — which is a guess, and it is wrong exactly when it matters.
 *
 * §9.1 V3 is unchanged and is the reason there is no `detail` prop: never the exception text, never
 * a status code, never a route. The support code is the one token that crosses, and it names a
 * request rather than describing a fault.
 */

const UNKNOWN_CAUSE_HE = 'לא הצלחנו להשלים את הפעולה, ולא ידוע לנו מה גרם לכך.';
const UNKNOWN_NEXT_HE =
  'אפשר לרענן את המסך, לבדוק את המצב העדכני, ולנסות שוב. אם הבעיה חוזרת — פנה לתמיכה עם הקוד שלמטה.';

export interface ErrorStateProps {
  readonly requestUid: string;
  /** One sentence: what happened. Omitted ⇒ the honest "we do not know". */
  readonly whatHappenedHe?: string;
  /** One sentence: what to do now. Omitted ⇒ refresh, check, retry, then support. */
  readonly whatToDoHe?: string;
  /** An action the person can take right here, when the screen has one — e.g. "start again". */
  readonly action?: ReactNode;
}

export function ErrorState({
  requestUid,
  whatHappenedHe,
  whatToDoHe,
  action,
}: ErrorStateProps): JSX.Element {
  return (
    <StateBlock
      titleHe="הפעולה לא הושלמה"
      bodyHe={whatHappenedHe ?? UNKNOWN_CAUSE_HE}
      tone="error"
      nextStep={
        <>
          <p className="state-block__todo">{whatToDoHe ?? UNKNOWN_NEXT_HE}</p>
          {action === undefined ? null : <div className="actions-row">{action}</div>}
          <SupportReference requestUid={requestUid} />
        </>
      }
    />
  );
}

/*
 * `SupportReference` LIVES IN ITS OWN FILE SINCE 2026-10-28, because a refusal needs it too
 * (`DenialNotice`, Owner section 5). One component rather than two copies of the clipboard
 * handling, the live region, the failure wording and the empty case.
 */
